import { useCallback } from 'react';
import styled from 'styled-components';
import partial from 'transmute/partial';
import { DefaultTableColumns, DEAL_GROUPING_SUFFIX } from '../../constants/OverviewTableConstants';
import NameCell from './NameCell';
import AttainmentCell from './AttainmentCell';
import CoverageCell from './CoverageCell';
import CoverageRatioCell from './CoverageRatioCell';
import DealRollupCell from './DealRollupCell';
import ForecastCell from './ForecastCell';
import StickyCellForecastColumn from '../styled/StickyCellForecastColumn';
import { getDealGrouping } from '../../utils/dealGroupingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const AccentedRow = styled.tr.withConfig({
  displayName: "RepOverviewTableRow__AccentedRow",
  componentId: "xs1u6k-0"
})(["background:white;"]);
const RepOverviewTableRow = ({
  closedDealGroupingIds,
  columns,
  dealRollupData,
  dealRollupGroupings,
  isDetailPage,
  onAttainmentBarClick,
  onDealCountClick,
  onEditForecastClick,
  onNameCellViewDealsClick,
  onRowClick,
  rowData
}) => {
  const renderNameCell = useCallback(() => {
    return /*#__PURE__*/_jsx(NameCell, {
      isDetailPage: isDetailPage,
      onNameCellViewDealsClick: onNameCellViewDealsClick,
      onRowClick: onRowClick,
      rowData: rowData
    }, DefaultTableColumns.NAME);
  }, [isDetailPage, onNameCellViewDealsClick, onRowClick, rowData]);
  const renderAttainmentCell = useCallback(() => {
    const {
      id,
      isUser
    } = rowData;
    return /*#__PURE__*/_jsx(AttainmentCell, {
      isDetailPage: isDetailPage,
      onClickAttainmentBar: partial(onAttainmentBarClick, closedDealGroupingIds, id, isUser),
      rowData: rowData,
      subscreen: "table-row"
    }, DefaultTableColumns.ATTAINMENT);
  }, [closedDealGroupingIds, isDetailPage, onAttainmentBarClick, rowData]);
  const renderCoverageCell = useCallback(() => {
    return /*#__PURE__*/_jsx(CoverageCell, {
      isDetailPage: isDetailPage,
      rowData: rowData
    }, DefaultTableColumns.COVERAGE);
  }, [isDetailPage, rowData]);
  const renderCoverageRatioCell = useCallback(() => {
    return /*#__PURE__*/_jsx(CoverageRatioCell, {
      coverage: rowData.coverage
    }, DefaultTableColumns.COVERAGE_RATIO);
  }, [rowData.coverage]);
  const renderDealRollupCell = useCallback(groupingId => {
    const dealRollupGrouping = getDealGrouping({
      groupingId,
      dealGroupings: dealRollupGroupings
    });
    if (!dealRollupGrouping) {
      return null;
    }
    return /*#__PURE__*/_jsx(DealRollupCell, {
      dealRollupData,
      groupingId,
      key: `${groupingId}${DEAL_GROUPING_SUFFIX}`,
      onDealCountClick,
      rowData
    });
  }, [dealRollupData, dealRollupGroupings, onDealCountClick, rowData]);
  const renderForecastCell = useCallback(() => {
    return /*#__PURE__*/_jsx(StickyCellForecastColumn, {
      children: /*#__PURE__*/_jsx(ForecastCell, {
        onEditForecastClick: onEditForecastClick,
        rowData: rowData
      })
    }, DefaultTableColumns.FORECAST);
  }, [onEditForecastClick, rowData]);
  const keyToCellRendererMap = {
    [DefaultTableColumns.ATTAINMENT]: renderAttainmentCell,
    [DefaultTableColumns.COVERAGE]: renderCoverageCell,
    [DefaultTableColumns.COVERAGE_RATIO]: renderCoverageRatioCell,
    [DefaultTableColumns.FORECAST]: renderForecastCell,
    [DefaultTableColumns.NAME]: renderNameCell
  };
  const {
    id
  } = rowData;
  return /*#__PURE__*/_jsx(AccentedRow, {
    "data-test-id": rowData.name,
    children: columns.map(column => {
      const renderCell = keyToCellRendererMap[column.value];
      return renderCell !== undefined && column.internal ? renderCell() : renderDealRollupCell(column.value);
    })
  }, id);
};
export default RepOverviewTableRow;